import { useState } from 'react'

import { ShiftTemplate } from '@/core/models/shift.model'
import { useGetEligibleStaffsQuery } from '@/core/services/planning'

import { ShiftTemplateDialog } from './ShiftTemplateDialog'
import { useCreateShift } from '../model/useCreateShift'
import { useAdminClaimShift } from '../model/useAdminClaimShift'
import { EditShiftTemplateDialog } from './EditShiftTemplateDialog'
import { DeleteShiftTemplateDialog } from './DeleteShiftTemplateDialog'

type DialogState = 'edit-shift-template' | 'delete-shift-template' | 'none'

type ShiftTemplateDetailsProps = {
  open: boolean
  onClose: () => void
  shiftTemplate: ShiftTemplate
  selectedStart: Date
  selectedEnd: Date
  planningId: string
  onShiftCreated?: () => void
  isAdmin?: boolean
}

export function ShiftTemplateDetails({
  open,
  onClose,
  shiftTemplate,
  selectedStart,
  selectedEnd,
  planningId,
  onShiftCreated,
  isAdmin = false,
}: ShiftTemplateDetailsProps) {
  const shiftTemplateId = shiftTemplate._id
  const startTime = selectedStart.toISOString()
  const endTime = selectedEnd.toISOString()

  const [dialogState, setDialogState] = useState<DialogState>('none')

  const [createShift, isCreatingShift] = useCreateShift(planningId, shiftTemplateId, startTime, endTime)
  const [adminClaimShift, isClaimingShift] = useAdminClaimShift(planningId, shiftTemplateId, startTime, endTime)

  const { data: eligibleStaffs = [] } = useGetEligibleStaffsQuery(
    {
      planningId,
      shiftTemplateId,
      startTime,
      endTime,
    },
    { skip: !isAdmin || !open }
  )

  const closeDialog = () => setDialogState('none')

  const handleConfirm = (staffIds: string[]) => {
    if (isAdmin) {
      adminClaimShift(staffIds, onShiftCreated)
    } else {
      createShift(onShiftCreated)
    }
  }

  return (
    <>
      <ShiftTemplateDialog
        open={open}
        onClose={onClose}
        onConfirm={handleConfirm}
        onDeleteClick={() => setDialogState('delete-shift-template')}
        onEditClick={() => setDialogState('edit-shift-template')}
        shiftTemplate={shiftTemplate}
        selectedStart={selectedStart}
        selectedEnd={selectedEnd}
        isDisabled={isCreatingShift || isClaimingShift}
        eligibleStaff={eligibleStaffs}
        isAdmin={isAdmin}
      />
      <EditShiftTemplateDialog
        open={dialogState === 'edit-shift-template'}
        onClose={closeDialog}
        shiftTemplate={shiftTemplate}
        planningId={planningId}
        onSuccess={closeDialog}
      />
      <DeleteShiftTemplateDialog
        open={dialogState === 'delete-shift-template'}
        onClose={closeDialog}
        planningId={planningId}
        shiftTemplateId={shiftTemplate._id}
        startTime={selectedStart.toISOString()}
        endTime={selectedEnd.toISOString()}
      />
    </>
  )
}
